import { NetworkStatus, useQuery } from '@apollo/client'
import { QueryHookOptions } from '@apollo/client/react/types/types'
import useTokensVolume24hs from '@swarm/core/hooks/data/useTokensVolume24hs'
import { StakesQuery } from '@swarm/core/queries'
import { POLL_INTERVAL } from '@swarm/core/shared/consts'
import { ZERO } from '@swarm/core/shared/utils/helpers/big-helpers'
import { normalizePoorSecurity } from '@swarm/core/shared/utils/subgraph/staking'
import { NormalizedStake } from '@swarm/types/normalized-entities'
import {
  StakesQueryVariables,
  SwarmStakeFilter,
  SwarmStake as SwarmStakeSubgraph,
} from '@swarm/types/subgraph/graphql'
import uniqBy from 'lodash/uniqBy'
import { useCallback, useMemo } from 'react'

import { PageLimit } from 'src/components/Staking/consts'

interface StakesResponse {
  swarmStakes: SwarmStakeSubgraph[]
}

const useStakes = (
  options?: QueryHookOptions<StakesResponse, StakesQueryVariables>,
) => {
  const {
    data,
    loading,
    networkStatus,
    refetch: nativeRefetch,
    called,
    fetchMore: nativeFetchMore,
  } = useQuery<StakesResponse>(StakesQuery, {
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    pollInterval: POLL_INTERVAL,
    ...options,
    variables: {
      limit: PageLimit,
      skip: 0,
      ...options?.variables,
    },
  })

  const stakeTokens = useMemo(() => {
    if (!data?.swarmStakes) return []
    return uniqBy(
      data?.swarmStakes.map((stake) => stake.stakedSecurity.token),
      'id',
    )
  }, [data?.swarmStakes])

  const volumes = useTokensVolume24hs(stakeTokens)

  const fetchMore = useCallback(
    (skip: number, limit?: number) => {
      return nativeFetchMore({
        variables: {
          ...options?.variables,
          ...(limit && { limit }),
          skip,
        },
      })
    },
    [nativeFetchMore, options?.variables],
  )

  const refetch = useCallback(
    (filter: Partial<SwarmStakeFilter> = {}) =>
      nativeRefetch({ filter, limit: PageLimit, skip: 0 }),
    [nativeRefetch],
  )

  const stakes = useMemo<NormalizedStake[]>(() => {
    if (!volumes) return []
    return (
      data?.swarmStakes?.map((stake) => {
        const stakedSecurity = {
          ...normalizePoorSecurity(stake.stakedSecurity),
          volume24hs: volumes.get(stake.stakedSecurity.token.id) || ZERO,
        }

        return {
          id: stake.id,
          maker: stake.maker,
          stakedAmount: stake.stakedAmount,
          stakedAt: stake.stakedAt,
          stakedTx: stake.stakedTx,
          stakedSecurity,
        }
      }) || []
    )
  }, [data?.swarmStakes, volumes])

  const loadingStakes =
    loading ||
    ![NetworkStatus.ready, NetworkStatus.error, NetworkStatus.poll].includes(
      networkStatus,
    )

  return {
    stakes,
    called,
    loading: loadingStakes,
    refetching: networkStatus === NetworkStatus.refetch,
    fetchMore,
    refetch,
  }
}

export default useStakes
